import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { useTheme } from '../../context/ThemeContext';
import { SPECIALTY_TEMPLATES, GENERAL_AI_INSTRUCTIONS } from '../../data/specialtyTemplates';
import { 
  Globe, FileText, Upload, Image, Trash2, Plus, Save, 
  Loader2, CheckCircle, AlertCircle, Brain, MessageSquare,
  FileUp, Link as LinkIcon, Camera, Package, Stethoscope, ChevronDown
} from 'lucide-react';
import AIPreview from '../../components/dashboard/AIPreview';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Training() {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [clinicId, setClinicId] = useState('');
  const [showSpecialtySelector, setShowSpecialtySelector] = useState(false);
  const [selectedSpecialty, setSelectedSpecialty] = useState(null);
  const [doctorName, setDoctorName] = useState('');
  const [originalConfig, setOriginalConfig] = useState(null);
  const [config, setConfig] = useState({
    website_url: '',
    ai_instructions: '',
    ai_personality: '',
    documents: [],
    photos: [],
    inventory: [],
    specialty: '',
    handoff_enabled: true,
    handoff_phone: '',
    handoff_message: 'Un momento, te comunico con un asesor humano que podrá ayudarte mejor.'
  });
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [scrapingUrl, setScrapingUrl] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Calculate unsaved changes using useMemo instead of useEffect
  const hasUnsavedChanges = originalConfig 
    ? JSON.stringify(config) !== JSON.stringify(originalConfig) 
    : false;

  // Warn user before leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const storedClinicId = localStorage.getItem('clinicId');
      
      console.log('🔍 Loading training config with:', { storedClinicId, tokenExists: !!token });
      
      if (!token) {
        console.error('❌ No token found');
        setLoading(false);
        return;
      }
      
      const res = await fetch(`${API_URL}/api/training/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        console.log('✅ Training data received:', { 
          clinic_id: data.clinic_id, 
          services: data.inventory?.length || 0 
        });
        
        setClinicId(data.clinic_id);
        setDoctorName(data.doctor_name || '');
        
        // Set selected specialty if exists
        if (data.specialty && SPECIALTY_TEMPLATES[data.specialty]) {
          setSelectedSpecialty(SPECIALTY_TEMPLATES[data.specialty]);
        }
        
        const loadedConfig = {
          website_url: data.website_url || '',
          ai_instructions: data.ai_instructions || '',
          ai_personality: data.ai_personality || '',
          documents: data.documents || [],
          photos: data.photos || [],
          inventory: data.inventory || [],
          specialty: data.specialty || '',
          handoff_enabled: data.handoff_enabled !== false,
          handoff_phone: data.handoff_phone || '',
          handoff_message: data.handoff_message || 'Un momento, te comunico con un asesor humano que podrá ayudarte mejor.'
        };
        
        setConfig(loadedConfig);
        setOriginalConfig(loadedConfig);
      } else {
        console.error('❌ Error response:', res.status);
      }
    } catch (err) {
      console.error('❌ Error loading config:', err);
    }
    setLoading(false);
  };

  // Load config on mount
  useEffect(() => {
    loadConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply specialty template - ASKS for confirmation before replacing
  const applySpecialtyTemplate = (specialty) => {
    // If user has existing services, ask for confirmation
    if (config.inventory && config.inventory.length > 0 && specialty.id !== 'otra') {
      const confirmChange = window.confirm(
        `⚠️ Tienes ${config.inventory.length} servicios configurados.\n\n` +
        `¿Deseas reemplazarlos con el template de "${specialty.name}"?\n\n` +
        `Selecciona "Cancelar" para conservar tus servicios actuales.`
      );
      
      if (!confirmChange) {
        setShowSpecialtySelector(false);
        return;
      }
    }
    
    setSelectedSpecialty(specialty);
    setShowSpecialtySelector(false);
    
    if (specialty.id === 'otra') {
      // For "Otra", just set specialty but keep user's existing data
      setConfig(prev => ({
        ...prev,
        specialty: specialty.id
      }));
      setMessage({ type: 'success', text: 'Especialidad personalizada seleccionada. Tus servicios se conservan.' });
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
      return;
    }
    
    // REPLACE template completely - user confirmed
    const templateInstructions = specialty.instructions + '\n\n' + GENERAL_AI_INSTRUCTIONS;
    
    // Create new services from template - complete replacement
    const templateServices = specialty.services.map((s, idx) => ({
      id: `template_${specialty.id}_${idx}_${Date.now()}`,
      name: s.name,
      price: s.priceRange,
      description: s.description
    }));
    
    setConfig(prev => ({
      ...prev,
      specialty: specialty.id,
      ai_instructions: templateInstructions,
      ai_personality: specialty.personality,
      inventory: templateServices  // Complete replacement after confirmation
    }));
    
    setMessage({ type: 'success', text: `Template de ${specialty.name} aplicado. Personaliza los rangos de precio.` });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      // Always send all fields to ensure data integrity
      const dataToSave = {
        doctor_name: doctorName || '',
        website_url: config.website_url || '',
        ai_instructions: config.ai_instructions || '',
        ai_personality: config.ai_personality || '',
        inventory: config.inventory || [],
        specialty: config.specialty || '',
        documents: config.documents || [],
        photos: config.photos || [],
        handoff_enabled: config.handoff_enabled !== false,
        handoff_phone: config.handoff_phone || '',
        handoff_message: config.handoff_message || 'Un momento, te comunico con un asesor humano que podrá ayudarte mejor.',
        greeting_message: config.greeting_message || ''
      };
      
      console.log('💾 Saving config:', { 
        specialty: dataToSave.specialty,
        inventoryCount: dataToSave.inventory.length,
        doctorName: dataToSave.doctor_name
      });
      
      const res = await fetch(`${API_URL}/api/training/config`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify(dataToSave)
      });
      if (res.ok) {
        setMessage({ type: 'success', text: '¡Configuración guardada exitosamente!' });
        setOriginalConfig({...config}); // Update original to current - this resets hasUnsavedChanges
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      } else {
        const errorData = await res.json().catch(() => ({}));
        setMessage({ type: 'error', text: errorData.detail || 'Error al guardar configuración' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Error al guardar la configuración' });
    }
    setSaving(false);
  };

  const scrapeWebsite = async () => {
    if (!config.website_url) return;
    setScrapingUrl(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/training/scrape-website`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ url: config.website_url })
      });
      if (res.ok) {
        const data = await res.json();
        setMessage({ type: 'success', text: `Información extraída: ${data.content_length} caracteres` });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Error al extraer información del sitio' });
    }
    setScrapingUrl(false);
  };

  const handleDocumentUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (config.documents.length + files.length > 10) {
      setMessage({ type: 'error', text: 'Máximo 10 documentos permitidos' });
      return;
    }

    setUploadingDoc(true);
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/training/upload-document`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        });
        
        if (res.ok) {
          const data = await res.json();
          setConfig(prev => ({
            ...prev,
            documents: [...prev.documents, {
              id: data.id,
              name: file.name,
              size: file.size,
              uploaded_at: new Date().toISOString()
            }]
          }));
        }
      } catch (err) {
        console.error('Error uploading:', err);
      }
    }
    setUploadingDoc(false);
    setMessage({ type: 'success', text: 'Documentos subidos correctamente' });
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    
    setUploadingPhoto(true);
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/training/upload-photo`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        });
        
        if (res.ok) {
          const data = await res.json();
          setConfig(prev => ({
            ...prev,
            photos: [...prev.photos, {
              id: data.id,
              name: file.name,
              url: data.url,
              uploaded_at: new Date().toISOString()
            }]
          }));
        }
      } catch (err) {
        console.error('Error uploading photo:', err);
      }
    }
    setUploadingPhoto(false);
  };

  const removeDocument = (docId) => {
    setConfig(prev => ({
      ...prev,
      documents: prev.documents.filter(d => d.id !== docId)
    }));
  };

  const removePhoto = (photoId) => {
    setConfig(prev => ({
      ...prev,
      photos: prev.photos.filter(p => p.id !== photoId)
    }));
  };

  const addInventoryItem = () => {
    setConfig(prev => ({
      ...prev,
      inventory: [...prev.inventory, { id: Date.now(), name: '', price: '', description: '' }]
    }));
  };

  const updateInventoryItem = (id, field, value) => {
    setConfig(prev => ({
      ...prev,
      inventory: prev.inventory.map(item => 
        item.id === id ? { ...item, [field]: value } : item
      )
    }));
  };

  const removeInventoryItem = (id) => {
    setConfig(prev => ({
      ...prev,
      inventory: prev.inventory.filter(item => item.id !== id)
    }));
  };

  const { theme } = useTheme();
  
  // Theme classes
  const cardBg = theme === 'dark' ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm';
  const inputBg = theme === 'dark' ? 'bg-slate-700/50 border-slate-600 text-white' : 'bg-gray-50 border-gray-300 text-gray-900';
  const textPrimary = theme === 'dark' ? 'text-white' : 'text-gray-900';
  const textSecondary = theme === 'dark' ? 'text-gray-400' : 'text-gray-600';
  const textMuted = theme === 'dark' ? 'text-gray-500' : 'text-gray-500';

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-2xl font-bold flex items-center gap-2 ${textPrimary}`}>
              <Brain className="w-7 h-7 text-purple-500" />
              Entrenamiento de IA
            </h1>
            <p className={`${textSecondary} mt-1`}>
              Personaliza cómo responde tu asistente de IA
            </p>
          </div>
          <div className="flex items-center gap-3">
            {hasUnsavedChanges && (
              <span className="text-yellow-500 text-sm flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                Cambios sin guardar
              </span>
            )}
            <Button 
              onClick={saveConfig} 
              disabled={saving}
              className={`text-white ${hasUnsavedChanges 
                ? 'bg-gradient-to-r from-yellow-500 to-orange-500 animate-pulse' 
                : 'bg-gradient-to-r from-blue-600 to-cyan-600'}`}
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {hasUnsavedChanges ? '¡Guardar Ahora!' : 'Guardar Cambios'}
            </Button>
          </div>
        </div>

        {/* Message */}
        {message.text && (
          <div className={`p-4 rounded-lg flex items-center gap-2 ${
            message.type === 'success' ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'
          }`}>
            {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
            {message.text}
          </div>
        )}

        {/* Specialty Selector */}
        <Card className={cardBg}>
          <CardHeader>
            <CardTitle className={`flex items-center gap-2 text-lg ${textPrimary}`}>
              <Stethoscope className="w-5 h-5 text-blue-400" />
              Especialidad Médica
            </CardTitle>
            <CardDescription className={textSecondary}>
              Selecciona tu especialidad para cargar un template optimizado con FAQs, servicios y comportamiento del asistente
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="relative">
              <button
                onClick={() => setShowSpecialtySelector(!showSpecialtySelector)}
                className={`w-full p-4 rounded-lg border ${inputBg} flex items-center justify-between transition-colors hover:border-blue-500`}
              >
                <div className="flex items-center gap-3">
                  {selectedSpecialty ? (
                    <>
                      <span className="text-2xl">{selectedSpecialty.icon}</span>
                      <div className="text-left">
                        <p className={`font-medium ${textPrimary}`}>{selectedSpecialty.name}</p>
                        <p className={`text-sm ${textMuted}`}>{selectedSpecialty.description}</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <span className="text-2xl">🏥</span>
                      <div className="text-left">
                        <p className={`font-medium ${textPrimary}`}>Selecciona tu especialidad</p>
                        <p className={`text-sm ${textMuted}`}>Carga automáticamente FAQs y servicios</p>
                      </div>
                    </>
                  )}
                </div>
                <ChevronDown className={`w-5 h-5 ${textSecondary} transition-transform ${showSpecialtySelector ? 'rotate-180' : ''}`} />
              </button>

              {showSpecialtySelector && (
                <div className={`absolute z-50 w-full mt-2 rounded-lg border ${theme === 'dark' ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'} shadow-xl max-h-96 overflow-y-auto`}>
                  {Object.values(SPECIALTY_TEMPLATES).map((specialty) => (
                    <button
                      key={specialty.id}
                      onClick={() => applySpecialtyTemplate(specialty)}
                      className={`w-full p-4 flex items-center gap-3 transition-colors ${
                        theme === 'dark' ? 'hover:bg-slate-700' : 'hover:bg-gray-100'
                      } ${selectedSpecialty?.id === specialty.id ? (theme === 'dark' ? 'bg-blue-600/20' : 'bg-blue-50') : ''}`}
                    >
                      <span className="text-2xl">{specialty.icon}</span>
                      <div className="text-left flex-1">
                        <p className={`font-medium ${textPrimary}`}>{specialty.name}</p>
                        <p className={`text-sm ${textMuted}`}>{specialty.description}</p>
                      </div>
                      {selectedSpecialty?.id === specialty.id && (
                        <CheckCircle className="w-5 h-5 text-blue-500" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {selectedSpecialty && selectedSpecialty.id !== 'otra' && (
              <div className={`mt-4 p-3 rounded-lg ${theme === 'dark' ? 'bg-blue-500/10 border border-blue-500/30' : 'bg-blue-50 border border-blue-200'}`}>
                <p className={`text-sm ${theme === 'dark' ? 'text-blue-300' : 'text-blue-700'}`}>
                  <strong>✓ Template aplicado:</strong> Se cargaron {selectedSpecialty.services?.length || 0} servicios y las instrucciones de {selectedSpecialty.name}. 
                  Personaliza los rangos de precio en la sección de Inventario.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Website URL */}
          <Card className={cardBg}>
            <CardHeader>
              <CardTitle className={`flex items-center gap-2 text-lg ${textPrimary}`}>
                <Globe className="w-5 h-5 text-blue-400" />
                Sitio Web
              </CardTitle>
              <CardDescription className={textSecondary}>
                Extrae información automáticamente de tu sitio web
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input
                  value={config.website_url}
                  onChange={(e) => setConfig({ ...config, website_url: e.target.value })}
                  placeholder="https://tuclínica.com"
                  className={inputBg}
                />
                <Button 
                  onClick={scrapeWebsite}
                  disabled={scrapingUrl || !config.website_url}
                  variant="outline"
                >
                  {scrapingUrl ? <Loader2 className="w-4 h-4 animate-spin" /> : <LinkIcon className="w-4 h-4" />}
                </Button>
              </div>
              <p className={`text-xs ${textMuted}`}>
                La IA usará esta información para responder preguntas sobre tu clínica
              </p>
            </CardContent>
          </Card>

          {/* Documents Upload */}
          <Card className={cardBg}>
            <CardHeader>
              <CardTitle className={`flex items-center gap-2 text-lg ${textPrimary}`}>
                <FileText className="w-5 h-5 text-green-400" />
                Documentos ({config.documents.length}/10)
              </CardTitle>
              <CardDescription>
                Sube PDFs o Word con información de servicios, precios, etc.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <label className="flex items-center justify-center w-full h-24 border-2 border-dashed border-slate-600 rounded-lg cursor-pointer hover:border-blue-500 transition-colors">
                <input
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx"
                  onChange={handleDocumentUpload}
                  className="hidden"
                  disabled={uploadingDoc || config.documents.length >= 10}
                />
                {uploadingDoc ? (
                  <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                ) : (
                  <div className="text-center">
                    <FileUp className="w-8 h-8 mx-auto text-gray-500 mb-2" />
                    <span className="text-sm text-gray-500">Click para subir PDF o Word</span>
                  </div>
                )}
              </label>
              
              {/* Document list */}
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {config.documents.map(doc => (
                  <div key={doc.id} className="flex items-center justify-between p-2 bg-slate-700/50 rounded">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-blue-400" />
                      <span className="text-sm truncate max-w-[180px]">{doc.name}</span>
                    </div>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={() => removeDocument(doc.id)}
                      className="h-6 w-6 p-0 text-red-400 hover:text-red-300"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* AI Instructions */}
        <Card className={cardBg}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <MessageSquare className="w-5 h-5 text-purple-400" />
              Instrucciones para la IA
            </CardTitle>
            <CardDescription>
              Define cómo debe comportarse y responder tu asistente
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm text-gray-400 mb-2 block">Personalidad del asistente</label>
              <Input
                value={config.ai_personality}
                onChange={(e) => setConfig({ ...config, ai_personality: e.target.value })}
                placeholder="Ej: Amable, profesional, empático con los pacientes"
                className={inputBg}
              />
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-2 block">Instrucciones específicas</label>
              <Textarea
                value={config.ai_instructions}
                onChange={(e) => setConfig({ ...config, ai_instructions: e.target.value })}
                placeholder={`Ejemplo:
- Siempre saluda al paciente por su nombre
- Ofrece cita de valoración antes de cirugía
- Menciona que aceptamos todas las formas de pago
- Si preguntan por el Dr., decir que está disponible para consultas
- Para urgencias, dar el número de emergencias: 55-XXXX-XXXX`}
                className={`${inputBg} min-h-[150px]`}
              />
            </div>
          </CardContent>
        </Card>

        {/* Handoff Humano */}
        <Card className={cardBg}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-orange-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
              Transferencia a Humano
            </CardTitle>
            <CardDescription>
              Cuando el asistente no sepa responder, transferirá la conversación a un asesor humano
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.handoff_enabled}
                  onChange={(e) => setConfig({ ...config, handoff_enabled: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
              </label>
              <span className="text-sm text-gray-300">
                {config.handoff_enabled ? 'Habilitado' : 'Deshabilitado'}
              </span>
            </div>
            
            {config.handoff_enabled && (
              <>
                <div>
                  <label className="text-sm text-gray-400 mb-2 block">WhatsApp del asesor (opcional)</label>
                  <Input
                    value={config.handoff_phone}
                    onChange={(e) => setConfig({ ...config, handoff_phone: e.target.value })}
                    placeholder="Ej: +52 55 1234 5678"
                    className={inputBg}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Si lo dejas vacío, notificará en el mismo chat de WhatsApp Business
                  </p>
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-2 block">Mensaje de transferencia</label>
                  <Textarea
                    value={config.handoff_message}
                    onChange={(e) => setConfig({ ...config, handoff_message: e.target.value })}
                    placeholder="Un momento, te comunico con un asesor humano..."
                    className={inputBg}
                    rows={2}
                  />
                </div>
                <div className="p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                  <p className="text-sm text-orange-300">
                    <strong>¿Cuándo se activa?</strong> Cuando el paciente pregunte algo que no esté en el entrenamiento, 
                    pida hablar con una persona, o el asistente detecte que necesita ayuda humana.
                  </p>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Photos */}
        <Card className={cardBg}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Camera className="w-5 h-5 text-pink-400" />
              Fotos de la Clínica
            </CardTitle>
            <CardDescription>
              Sube fotos de tus instalaciones, equipo médico, etc.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              {config.photos.map(photo => (
                <div key={photo.id} className="relative group">
                  <img 
                    src={photo.url} 
                    alt={photo.name}
                    className="w-24 h-24 object-cover rounded-lg"
                  />
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => removePhoto(photo.id)}
                    className="absolute -top-2 -right-2 h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              ))}
              
              <label className="w-24 h-24 border-2 border-dashed border-slate-600 rounded-lg flex items-center justify-center cursor-pointer hover:border-blue-500 transition-colors">
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  className="hidden"
                  disabled={uploadingPhoto}
                />
                {uploadingPhoto ? (
                  <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                ) : (
                  <Plus className="w-6 h-6 text-gray-500" />
                )}
              </label>
            </div>
          </CardContent>
        </Card>

        {/* Inventory */}
        <Card className={cardBg}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Package className="w-5 h-5 text-orange-400" />
              Servicios / Inventario
            </CardTitle>
            <CardDescription>
              Lista de servicios, procedimientos y precios que la IA puede mencionar
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {config.inventory.map((item, idx) => (
              <div key={item.id} className="flex gap-3 items-start p-3 bg-slate-700/30 rounded-lg">
                <div className="flex-1 grid grid-cols-3 gap-3">
                  <Input
                    value={item.name}
                    onChange={(e) => updateInventoryItem(item.id, 'name', e.target.value)}
                    placeholder="Nombre del servicio"
                    className={inputBg}
                  />
                  <Input
                    value={item.price}
                    onChange={(e) => updateInventoryItem(item.id, 'price', e.target.value)}
                    placeholder="Precio (ej: $5,000 MXN)"
                    className={inputBg}
                  />
                  <Input
                    value={item.description}
                    onChange={(e) => updateInventoryItem(item.id, 'description', e.target.value)}
                    placeholder="Descripción breve"
                    className={inputBg}
                  />
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => removeInventoryItem(item.id)}
                  className="text-red-400 hover:text-red-300"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
            
            <Button
              variant="outline"
              onClick={addInventoryItem}
              className="w-full border-dashed"
            >
              <Plus className="w-4 h-4 mr-2" />
              Agregar Servicio
            </Button>
          </CardContent>
        </Card>

        {/* AI Preview/Sandbox */}
        {clinicId && (
          <AIPreview clinicId={clinicId} />
        )}
      </div>
    </DashboardLayout>
  );
}
